import React, { useState, useEffect } from 'react';
import { FiMapPin, FiClock, FiPhone } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { apiGet } from '../services/apiService';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await apiGet('treatment_centers');
        // Transform API data to match component structure
        const transformedLocations = data.centers.map(center => ({
          city: center.name,
          address: center.address,
          timings: center.hours,
          phone: center.phone,
          mapEmbed: center.location_link
        }));
        setLocations(transformedLocations);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
        // Fallback to empty array
        setLocations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  if (loading) {
    return (
      <section id="locations" className="py-16 lg:py-24 bg-gradient-to-br from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading treatment centers...</p>
          </div>
        </div>
      </section>
    );
  }

  const locationsData = [
    {
      city: 'Mumbai (Mulund East)',
      address: 'D-3 Plot - 3, Mulund Kalash CHS Near Eastern Express Highway MHADA Colony Mulund East Mumbai 400081',
      timings: '10:00AM to 08:00PM',
      phone: '9581556660',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3768.123!2d72.96!3d19.17!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b9123456789a%3A0x123456789abcdef!2sMulund%20East%2C%20Mumbai!5e0!3m2!1sen!2sin!4v1647875555555'
    },
    {
      city: 'Lonavala / Kamshet',
      address: 'Shop no 03 milkat no.104, railway station, bazar peth, near bank of maharashtra, near kamshet, pune 410405',
      timings: '10:00AM to 08:00PM',
      phone: '9581556660',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3778.123!2d73.5!3d18.7!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b9123456789a%3A0x123456789abcdef!2sKamshet!5e0!3m2!1sen!2sin!4v1647875666666'
    },
    {
      city: 'Hyderabad',
      address: '15 -143 , New Mirjalaguda, Malkajgiri, Hyderabad, 500047',
      timings: '09:00AM to 08:00PM',
      phone: '9581556660',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.123!2d78.5!3d17.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b9123456789a%3A0x123456789abcdef!2sMalkajgiri!5e0!3m2!1sen!2sin!4v1647875777777'
    },
    {
      city: 'Uttar Pradesh (Azamgarh)',
      address: 'Ground Floor Hydil Chauraha Sidhari Halt Sadar Azamgarh Uttar Pradesh 276001 India',
      timings: '10:00AM to 08:00PM',
      phone: '9581556660',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3591.123!2d83.1!3d26.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b9123456789a%3A0x123456789abcdef!2sAzamgarh!5e0!3m2!1sen!2sin!4v1647875888888'
    },
    {
      city: 'Chhattisgarh (Raipur)',
      address: 'Shiv Aradhana MIG 55 Sector 03, Near Main Road Deen Dayal Upadhyay Nagar, Raipur 492001',
      timings: '10:00AM to 08:00PM',
      phone: '9581556660',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3718.123!2d81.6!3d21.2!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b9123456789a%3A0x123456789abcdef!2sRaipur!5e0!3m2!1sen!2sin!4v1647875999999'
    }
  ];

  return (
    <section id="locations" className="py-16 lg:py-24 bg-gradient-to-br from-emerald-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            Our <span className="text-emerald-600">Treatment Centers</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Find us across 5 Indian states. Each facility is highly respected and equipped for non-surgical spinal healing.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(locations.length > 0 ? locations : locationsData).map((location, index) => (
            <div key={index} className="bg-white rounded-[2rem] shadow-xl overflow-hidden border border-emerald-50 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="h-48 bg-emerald-100 relative group">
                <iframe 
                  src={location.mapEmbed} 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen="" 
                  loading="lazy" 
                  className="transition-all duration-700"
                ></iframe>
                <div className="absolute top-4 right-4 bg-emerald-600 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-lg">
                  Verified Center
                </div>
              </div>
              <div className="p-8 space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{location.city}</h3>
                  <div className="flex items-start space-x-3 group">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                    <p className="text-gray-600 text-sm leading-relaxed">{location.address}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiClock} className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-700 font-medium text-sm">{location.timings}</span>
                </div>
                <div className="flex items-center space-x-3 pt-4 border-t border-gray-100">
                  <SafeIcon icon={FiPhone} className="w-5 h-5 text-emerald-600" />
                  <a href={`tel:${location.phone}`} className="text-emerald-700 font-bold hover:text-emerald-800 transition-colors">
                    {location.phone}
                  </a>
                </div>
                <button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95">
                  Get Directions
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Locations;